<template>
  <div class="nd-page patient-details-page">
    <v-card class="nd-section-card patient-form-card">
      <v-form ref="form" v-model="dataForm.validForm">
        <div class="patient-card-header">
          <div>
            <h2>Dados do paciente</h2>
            <p>Identificação, vínculo familiar e dados cadastrais principais.</p>
          </div>
          <v-chip v-if="!page.isNew && dataset.data.id" class="patient-id-chip" small label>
            Paciente #{{ dataset.data.id }}
          </v-chip>
        </div>

        <section class="patient-form-section">
          <div class="patient-form-grid">
            <v-text-field
              id="name"
              label="Nome"
              v-model.lazy="dataset.data.name"
              :rules="validationRules.name"
              outlined
              dense hide-details="auto"
              class="patient-grid-span-2"
            ></v-text-field>
            <v-text-field
              id="registration"
              label="CPF/CNPJ"
              v-mask="['###.###.###-##', '##.###.###/####-##']"
              v-model.lazy="dataset.data.registration"
              :rules="validationRules.registration"
              outlined
              dense hide-details="auto"
            ></v-text-field>
            <v-text-field
              id="nationality"
              label="Nacionalidade"
              v-model="dataset.data.nationality"
              outlined
              dense hide-details="auto"
            ></v-text-field>
            <v-select
              id="gender"
              :items="dataset.genderTypes"
              label="Gênero"
              v-model="dataset.data.gender"
              item-value="id"
              item-text="name"
              outlined
              dense hide-details="auto"
            ></v-select>
            <v-text-field
              id="birthday"
              label="Data de nascimento"
              v-model="dataset.data.birthday"
              @change="syncGuardianState"
              outlined
              dense hide-details="auto"
              type="date"
            ></v-text-field>
            <v-select
              id="company"
              label="Empresa"
              v-model="dataset.data.companyId"
              :items="dataset.companies"
              item-text="name"
              item-value="id"
              outlined
              dense hide-details="auto"
              no-data-text="Nenhuma empresa encontrada"
            ></v-select>
            <v-text-field
              id="father"
              label="Nome do pai"
              v-model="dataset.data.father"
              outlined
              dense hide-details="auto"
            ></v-text-field>
            <v-text-field
              id="mother"
              label="Nome da mãe"
              v-model="dataset.data.mother"
              outlined
              dense hide-details="auto"
            ></v-text-field>
          </div>
        </section>

        <section class="patient-form-section patient-form-section--compact">
          <v-switch
            v-model="dataset.data.hasGuardian"
            label="Paciente possui responsável"
            color="primary"
            inset
            hide-details="auto"
            @change="onGuardianToggle"
          ></v-switch>
        </section>

        <section v-if="showGuardianSection" class="patient-form-section patient-form-section--warning">
          <div class="patient-section-title">
            <v-icon color="warning" size="20">mdi-account-child</v-icon>
            <div>
              <h3>Responsável pelo paciente</h3>
              <p>{{ guardianSectionDescription }}</p>
            </div>
          </div>
          <div class="patient-form-grid patient-form-grid--three">
            <v-text-field
              label="Nome do responsável"
              v-model="dataset.data.guardianName"
              :rules="validationRules.guardianName"
              outlined
              dense hide-details="auto"
            ></v-text-field>
            <v-text-field
              label="CPF do responsável"
              v-model="dataset.data.guardianCpf"
              v-mask="'###.###.###-##'"
              :rules="validationRules.guardianCpf"
              outlined
              dense hide-details="auto"
            ></v-text-field>
            <v-text-field
              label="Telefone do responsável"
              v-model="dataset.data.guardianPhone"
              v-mask="['(##) ####-####', '(##) #####-####']"
              :rules="validationRules.guardianPhone"
              outlined
              dense hide-details="auto"
            ></v-text-field>
          </div>
        </section>

        <section class="patient-form-section patient-form-section--address">
          <div class="patient-section-title">
            <div>
              <h3>Endereço</h3>
              <p>Localização e dados complementares do cadastro.</p>
            </div>
          </div>

          <div class="patient-form-grid patient-form-grid--address">
            <v-text-field
              id="zipCode"
              label="CEP"
              v-model="dataset.data.address.zipCode"
              outlined
              dense hide-details="auto"
              v-mask="'#####-###'"
              :rules="[
                (v) =>
                  !v ||
                  v.toString().replace(/\D/g, '').length === 8 ||
                  'O CEP deve ter 8 dígitos',
              ]"
              append-icon="mdi-magnify"
              @blur="fetchAddressByCep"
              @click:append="fetchAddressByCep"
            ></v-text-field>
            <v-text-field
              id="country"
              label="País"
              v-model="dataset.data.address.country"
              outlined
              dense hide-details="auto"
            ></v-text-field>
            <v-text-field
              id="state"
              label="Estado"
              v-model="dataset.data.address.state"
              outlined
              dense hide-details="auto"
            ></v-text-field>
            <v-text-field
              id="city"
              label="Cidade"
              v-model="dataset.data.address.city"
              outlined
              dense hide-details="auto"
            ></v-text-field>
            <v-text-field
              id="district"
              label="Bairro"
              v-model="dataset.data.address.district"
              outlined
              dense hide-details="auto"
            ></v-text-field>
            <v-text-field
              id="street"
              label="Rua"
              v-model="dataset.data.address.street"
              outlined
              dense hide-details="auto"
              class="patient-grid-span-2"
            ></v-text-field>
            <v-text-field
              id="number"
              label="Número"
              v-model="dataset.data.address.number"
              outlined
              dense hide-details="auto"
            ></v-text-field>
            <v-text-field
              id="complement"
              label="Complemento"
              v-model="dataset.data.address.complement"
              outlined
              dense hide-details="auto"
              class="patient-grid-span-2"
            ></v-text-field>
          </div>
        </section>

        <div class="nd-form-actions patient-form-actions">
          <v-btn id="btnBack" type="button" elevation="0" color="primary" outlined to="/pacientes">
            Voltar
          </v-btn>
          <v-btn
            id="btnSave"
            type="button"
            elevation="0"
            color="primary"
            @click="savePatient"
            :loading="dataset.loading"
          >
            Salvar
          </v-btn>
        </div>
      </v-form>
    </v-card>
  </div>
</template>

<script>
import string from "@/common/util/string";
import object from "@/common/util/object";

import axios from "axios";
import genderTypes from "@/common/data/gender-types.data";

export default {
  name: "PatientDetails",

  data: () => ({
    loadingPage: false,
    formValid: false, 
    birthdayMenu: false, 

    page: {
      title: "",
      isNew: true,
    },
    dataset: {
      data: {
        id: null,
        name: null,
        registration: null,
        gender: null,
        father: null,
        mother: null,
        birthday: null,
        nationality: null,
        companyId: null,
        guardianName: null,
        guardianCpf: null,
        guardianPhone: null,
        hasGuardian: false,
        address: {
          zipCode: null,
          country: null,
          state: null,
          city: null,
          district: null,
          street: null,
          number: null,
          complement: null,
        },
      },
      breadcrumbItems: [],
      loading: false, 
      submit: {
        brand: null, 
      },
      genderTypes,
      companies: [],
    },
    dataForm: {
      validForm: true,
    },
  }),


  async created() {
    this.loadingPage = true;

    const patientId = this.$route.params.id;
    this.page.isNew = !patientId;

    this.setPage();
    this.listBreadCrumbItems();

    await this.loadCompanies();

    if (!this.page.isNew) {
      await this.loadPatient(patientId); 
    }

    this.loadingPage = false; 
  },

  watch: {
    "dataset.data.address.zipCode"(newVal) {
      const cep = (newVal || "").replace(/\D/g, "");
      if (cep.length === 8 && this.page.isNew) {
        this.fetchAddressByCep();
      }
    },

    "dataset.data.birthday"() {
      this.syncGuardianState();
    },
  },

  computed: {
    validationRules() {
      return {
        name: [
          (v) => !!String(v || "").trim() || "Informe o nome do paciente.",
        ],
        registration: [
          (v) => !!String(v || "").trim() || "Informe o CPF/CNPJ do paciente.",
          (v) => {
            const digits = String(v || "").replace(/\D/g, "");
            return !digits || [11, 14].includes(digits.length) || "Informe um CPF/CNPJ válido.";
          },
        ],
        guardianName: [
          (v) => !this.showGuardianSection || !!String(v || "").trim() || "Informe o nome do responsável.",
        ],
        guardianCpf: [
          (v) => !this.showGuardianSection || !!String(v || "").trim() || "Informe o CPF do responsável.",
          (v) => {
            const digits = String(v || "").replace(/\D/g, "");
            return !digits || digits.length === 11 || "Informe um CPF válido para o responsável.";
          },
        ],
        guardianPhone: [
          (v) => {
            const digits = String(v || "").replace(/\D/g, "");
            return !digits || [10, 11].includes(digits.length) || "Informe um telefone válido para o responsável.";
          },
        ],
      };
    },
    formattedBirthday() {
      if (!this.dataset.data.birthday) return null;
      return new Date(this.dataset.data.birthday).toLocaleDateString('pt-BR');
    },
    isMinorPatient() {
      if (!this.dataset.data.birthday) return false;
      const birthday = new Date(this.dataset.data.birthday);
      if (isNaN(birthday.getTime())) return false;
      const today = new Date();
      let age = today.getFullYear() - birthday.getFullYear();
      const m = today.getMonth() - birthday.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthday.getDate())) age--;
      return age < 18;
    },
    hasGuardianData() {
      return !!(
        String(this.dataset.data.guardianName || "").trim() ||
        String(this.dataset.data.guardianCpf || "").trim() ||
        String(this.dataset.data.guardianPhone || "").trim()
      );
    },
    showGuardianSection() {
      return !!(this.dataset.data.hasGuardian || this.isMinorPatient || this.hasGuardianData);
    },
    guardianSectionDescription() {
      if (this.isMinorPatient) {
        return "Nome e CPF do responsável são obrigatórios para pacientes menores de 18 anos.";
      }
      return "Informe os dados quando o atendimento ou a cobrança dependerem de um responsável.";
    },
  },

  methods: {
    goBack() {
      this.$router.replace("/pacientes");
    },

    // async
    loadData() {
      this.setPage();
      this.listBreadCrumbItems();

      this.load();
    },

    setPage() {
      this.page.title = this.page.isNew
        ? "Novo paciente"
        : "Detalhes do paciente";
    },

    listBreadCrumbItems() {
      this.dataset.breadcrumbItems = [
        {
          text: "Homepage",
          disabled: false,
          to: "/home",
          exact: true,
        },
      ];

      this.dataset.breadcrumbItems.push({
        text: "Pacientes",
        disabled: false,
        to: "/pacientes",
        exact: true,
      });

      this.dataset.breadcrumbItems.push({
        text: this.page.isNew ? "Novo paciente" : "Detalhes do paciente",
        disabled: true,
        exact: true,
      });
    },

    async loadPatient(id) {
      try {
        const response = await axios.get(`/patients/${id}`);

        if (!response.data || !response.data.data) {
          console.error("Dados do paciente não encontrados na resposta da API ou formato inesperado.", response.data);
          this.$dialog.notify.error("Formato de resposta da API inesperado para o paciente.", { position: "top-right", timeout: 5000 });
          this.$router.replace("/pacientes");
          return;
        }

        const patientApiData = response.data.data;

        this.dataset.data = {
          ...patientApiData,

          address: {
            ...this.dataset.data.address,
            ...(patientApiData.address || {}),
          },

          companyId: Number(patientApiData.companyId || patientApiData.company?.id) || null,

          gender: Number(patientApiData.gender) || null,

          phoneNumber: patientApiData.phone ? patientApiData.phone.value : null,

          guardianName: patientApiData.guardianName || null,
          guardianCpf: patientApiData.guardianCpf || null,
          guardianPhone: patientApiData.guardianPhone || null,
          hasGuardian: !!(patientApiData.guardianName || patientApiData.guardianCpf || patientApiData.guardianPhone),
        };

        if (this.dataset.data.birthday) {
          this.dataset.data.birthday = patientApiData.birthday.split('T')[0];
        }

        this.syncGuardianState();

      } catch (error) {
        console.error("Erro ao carregar paciente:", error);
        this.$dialog.notify.error(
          "Ocorreu um erro ao carregar as informações do paciente. Tente novamente!",
          { position: "top-right", timeout: 5000 }
        );
        this.$router.replace("/pacientes"); 
      }
    },

    async savePatient() {
      if (!this.$refs.form.validate()) {
        this.$dialog.notify.warn('Preencha todos os campos obrigatórios.', { position: 'top-right', timeout: 3000 });
        return;
      }

      const confirmText = this.page.isNew
        ? "Deseja criar este paciente?"
        : "Deseja atualizar os dados do paciente?";
      const confirmTitle = this.page.isNew
        ? "Criar Paciente"
        : "Atualizar Paciente";
      const successMessage = this.page.isNew
        ? "Paciente criado com sucesso!"
        : "Paciente atualizado com sucesso!";

      const proceedAction = await this.$dialog.confirm({
        text: confirmText,
        title: confirmTitle,
        actions: {
          false: "Cancelar",
          true: this.page.isNew ? "Criar" : "Atualizar",
        },
      });

      if (!proceedAction) return;

      this.dataset.loading = true; 

      const payload = this.buildPayload();

      try {
        let response;
        if (this.page.isNew) {
          response = await axios.post("/patients", payload);
          this.$router.replace(`/pacientes/${response.data.id || response.data.data.id}`); 
        } else {
          response = await axios.patch(`/patients/${this.$route.params.id}`, payload);
        }

        this.$dialog.notify.success(successMessage, {
          position: "top-right",
          timeout: 3000,
        });

      } catch (error) {
        console.error("Erro ao salvar paciente:", error);

        const errorMessage = this.$codes
          ? this.$codes(error)
          : 'Ocorreu um erro ao salvar o paciente.';

        this.$dialog.notify.error(errorMessage, {
          position: "top-right",
          timeout: 5000
        });

      } finally {
        this.dataset.loading = false;
      }
    },

    async loadCompanies() {
      try {
        const response = await axios.get("/company"); 
        this.dataset.companies = response.data.data || response.data; 
      } catch (error) {
        console.error("Erro ao carregar empresas:", error);
        this.$dialog.notify.error(
          "Ocorreu um erro ao carregar a lista de empresas.",
          { position: "top-right", timeout: 5000 }
        );
      }
    },

    // buildPayload() {
    //   const data = {
    //     identificador: this.dataset.data.name,
    //     registration: this.dataset.data.registration,
    //     nationality: this.dataset.data.nationality,
    //     father: this.dataset.data.father,
    //     mother: this.dataset.data.mother,
    //   };

    //   return data;
    // },

    buildPayload() {
      const payload = {
        name: this.dataset.data.name,
        registration: this.dataset.data.registration,
        nationality: this.dataset.data.nationality,
        father: this.dataset.data.father,
        mother: this.dataset.data.mother,
        gender: this.dataset.data.gender,
        birthday: this.dataset.data.birthday,
        companyId: this.dataset.data.companyId,
        address: {
          zipCode: (this.dataset.data.address.zipCode || '').replace(/\D/g, ''),
          country: this.dataset.data.address.country,
          state: this.dataset.data.address.state,
          city: this.dataset.data.address.city,
          district: this.dataset.data.address.district,
          street: this.dataset.data.address.street,
          number: this.dataset.data.address.number,
          complement: this.dataset.data.address.complement,
        },
        guardianName: this.showGuardianSection ? (this.dataset.data.guardianName || null) : null,
        guardianCpf: this.showGuardianSection ? ((this.dataset.data.guardianCpf || '').replace(/\D/g, '') || null) : null,
        guardianPhone: this.showGuardianSection ? ((this.dataset.data.guardianPhone || '').replace(/\D/g, '') || null) : null,
      };
      return payload;
    },

    syncGuardianState() {
      if (this.isMinorPatient) {
        this.dataset.data.hasGuardian = true;
      }
    },

    onGuardianToggle(value) {
      if (value || this.isMinorPatient) {
        if (this.isMinorPatient) this.dataset.data.hasGuardian = true;
        return;
      }

      this.dataset.data.guardianName = null;
      this.dataset.data.guardianCpf = null;
      this.dataset.data.guardianPhone = null;
    },

    // onCepInput(e) {
    //   let value = e.replace(/\D/g, "").slice(0, 8);
    //   if (value.length > 5) {
    //     value = value.replace(/^(\d{5})(\d{1,3})/, "$1-$2");
    //   }
    //   this.dataset.data.address.zipCode = value;
    // },

    onGenderInput() {
      const value = this.dataset.data.gender;
      const genderName = genderTypes.filter((gender) => gender.id === value);
      this.dataset.data.gender = genderName;
    },

    async fetchAddressByCep() {
      const cep = (this.dataset.data.address.zipCode || "").replace(/\D/g, "");
      if (!cep) return;

      if (cep.length !== 8) {
        this.$dialog.notify.error("Digite um CEP válido com 8 dígitos.", {
          position: "top-right",
          timeout: 2000,
        });
        return;
      }
      try {
        const response = await axios.get(
          `https://viacep.com.br/ws/${cep}/json/`
        );
        if (response.data.erro) {
          this.$dialog.notify.error("CEP não encontrado.", {
            position: "top-right",
            timeout: 2000,
          });
          return;
        }
        // Preenche os campos de endereço com a resposta da API
        this.dataset.data.address.street = response.data.logradouro || "";
        this.dataset.data.address.district = response.data.bairro || "";
        this.dataset.data.address.city = response.data.localidade || "";
        this.dataset.data.address.state = response.data.estado || "";
        this.dataset.data.address.complement = response.data.complemento || "";
      } catch (error) {
        this.$dialog.notify.error("Erro ao buscar o endereço pelo CEP.", {
          position: "top-right",
          timeout: 2000,
        });
      }
    },
  },
};
</script>

<style lang="scss">
.patient-details-page {
  gap: 22px;
}

.patient-form-card {
  padding: 0;
  overflow: hidden;
}

.patient-card-header {
  align-items: flex-start;
  border-bottom: 1px solid #eef0ed;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 28px 32px 20px;
}

.patient-card-header h2,
.patient-section-title h3 {
  color: #17201c;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0;
}

.patient-card-header h2 {
  font-size: 22px;
  line-height: 1.15;
}

.patient-card-header p,
.patient-section-title p {
  color: #7b8580;
  line-height: 1.35;
  margin: 8px 0 0;
}

.patient-card-header p {
  font-size: 15px;
}

.patient-id-chip {
  background: #f1e8fb !important;
  color: #8f4bd0 !important;
  font-weight: 800;
}

.patient-form-section {
  padding: 24px 32px 8px;
}

.patient-form-section + .patient-form-section {
  border-top: 1px solid #eef0ed;
  margin-top: 16px;
  padding-top: 24px;
}

.patient-form-section--warning {
  background: #fffaf0;
}

.patient-form-section--address {
  padding-bottom: 26px;
}

.patient-section-title {
  align-items: flex-start;
  display: flex;
  gap: 10px;
  margin-bottom: 18px;
}

.patient-section-title h3 {
  font-size: 18px;
  line-height: 1.2;
}

.patient-section-title p {
  font-size: 14px;
}

.patient-form-grid {
  display: grid;
  gap: 16px 18px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.patient-form-grid--three,
.patient-form-grid--address {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.patient-grid-span-2 {
  grid-column: span 2;
}

.patient-form-actions {
  border-top: 1px solid #eef0ed;
  margin: 0;
  padding: 20px 32px 28px;
}

@media (max-width: 1120px) {
  .patient-form-grid,
  .patient-form-grid--three,
  .patient-form-grid--address {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 780px) {
  .patient-card-header,
  .patient-form-section,
  .patient-form-actions {
    padding-left: 20px;
    padding-right: 20px;
  }

  .patient-form-grid,
  .patient-form-grid--three,
  .patient-form-grid--address {
    grid-template-columns: 1fr;
  }

  .patient-grid-span-2 {
    grid-column: span 1;
  }
}

.access-container {
  border: 1px solid var(--v-gray-base) !important;
}

.custom-chip {
  background: var(--v-white-base) !important;
}

.title-page {
  color: var(--v-primary-base);
  // font-family: Montserrat;
  font-size: 40px;
  font-style: normal;
  font-weight: 700;
  line-height: 38px;
}

.section-info-title {
  color: var(--v-primary-base);
  // font-family: Montserrat;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
}

.section-info-value {
  // font-family: Montserrat;
  font-size: 14px;
  font-style: normal;
}
</style>
